import type { CognitoTokens } from '@/types/auth.types'

// ─────────────────────────────────────────────────────────────
// Cognito Hosted UI — Social Login (Google / Meta)
//
// Flow:
//   1. loginWithProvider() → redirect to Cognito Hosted UI
//   2. Cognito redirects back to /auth/callback?code=XXX
//   3. exchangeCodeForTokens(code) → CognitoTokens
//   4. Parse id_token to get user info + role
// ─────────────────────────────────────────────────────────────

const HOSTED_UI = import.meta.env.VITE_COGNITO_HOSTED_UI_DOMAIN as string
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string
const REDIRECT  = import.meta.env.VITE_COGNITO_REDIRECT_SIGNIN as string

export type SocialProvider = 'Google' | 'Facebook'

/** Redirect browser to Cognito Hosted UI for social login */
export function loginWithProvider(provider: SocialProvider): void {
  const params = new URLSearchParams({
    response_type:      'code',
    client_id:          CLIENT_ID,
    redirect_uri:       REDIRECT,
    identity_provider:  provider,
    scope:              'openid email profile',
  })

  window.location.href = `${HOSTED_UI}/oauth2/authorize?${params.toString()}`
}

/** Exchange authorization code for Cognito tokens */
export async function exchangeCodeForTokens(code: string): Promise<CognitoTokens> {
  const body = new URLSearchParams({
    grant_type:   'authorization_code',
    client_id:    CLIENT_ID,
    code,
    redirect_uri: REDIRECT,
  })

  const res = await fetch(`${HOSTED_UI}/oauth2/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body:    body.toString(),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.error_description ?? 'Token exchange failed')
  }

  return res.json() as Promise<CognitoTokens>
}

/** Parse base64-encoded JWT payload (no signature verification — trust Cognito) */
export function parseIdToken(idToken: string): Record<string, unknown> {
  const payload = idToken.split('.')[1]
  const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
  return JSON.parse(decoded) as Record<string, unknown>
}

/** Build logout URL (clears Cognito session) */
export function buildLogoutUrl(): string {
  const signoutUri = import.meta.env.VITE_COGNITO_REDIRECT_SIGNOUT as string
  const params = new URLSearchParams({
    client_id:    CLIENT_ID,
    logout_uri:   signoutUri,
  })
  return `${HOSTED_UI}/logout?${params.toString()}`
}
